import { AlertCircle, Check, ListPlus, Trash2 } from "lucide-react";
import { de, type ResolvedResult } from "@ainkauf/core";

const SYMBOL = { add: ListPlus, check: Check, remove: Trash2, create_list: ListPlus } as const;
const AKTION = { add: "", check: de.assistant.checkAction, remove: de.assistant.removeAction, create_list: de.assistant.createListAction };

/**
 * Was der Assistent verstanden hat, bevor etwas passiert.
 *
 * Die Karte ist kein Ladebalken, sondern eine Rückfrage: Unsicheres steht
 * sichtbar gelb, mit Alternativen zum Antippen. Erst "Übernehmen" legt an.
 * Ein Sprachmodell, das ungefragt in die Einkaufsliste schreibt, verliert
 * beim ersten Missverständnis das Vertrauen der Familie.
 */
export function AssistantSheet({
  gehoert,
  ergebnis,
  onApply,
  onClose,
  onReplace,
}: {
  gehoert: string;
  ergebnis: ResolvedResult;
  onApply: () => void;
  onClose: () => void;
  onReplace: (aktion: number, artikel: number, name: string) => void;
}) {
  const leer = ergebnis.actions.length === 0;

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end bg-ink/40" onClick={onClose} role="presentation">
      <div
        onClick={(event) => {
          event.stopPropagation();
        }}
        role="dialog"
        aria-label={de.assistant.preview}
        data-testid="assistant-sheet"
        className="flex max-h-[88dvh] flex-col gap-4 overflow-y-auto rounded-t-[var(--radius-xl)] bg-paper px-5 pt-5"
        style={{ paddingBottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="flex flex-col gap-1">
          <span aria-hidden className="mx-auto mb-1 h-1.5 w-12 rounded-full bg-paper-3" />
        <h2 className="text-xl">{de.assistant.preview}</h2>
          {gehoert.length > 0 && (
            <p className="text-sm text-ink-2">
              {de.assistant.heard}: „{gehoert}“
            </p>
          )}
        </div>

        {leer ? (
          <p className="karte px-5 py-8 text-center text-ink-2">{de.assistant.nothingUnderstood}</p>
        ) : (
          ergebnis.actions.map((action, aktionIndex) => {
            const Icon = SYMBOL[action.type];
            const unsicher = action.listConfidence < 0.7;

            return (
              <section key={`${action.type}-${String(aktionIndex)}`} className="flex flex-col gap-2" data-testid="assistant-action">
                <h3 className={`flex items-center gap-2 text-sm font-semibold ${unsicher ? "text-honey" : "text-ink-2"}`}>
                  <Icon size={16} strokeWidth={1.5} aria-hidden />
                  {AKTION[action.type] ? `${AKTION[action.type]}: ` : ""}
                  {de.assistant.toList(action.listName)}
                  {unsicher && <AlertCircle size={14} strokeWidth={2} aria-label={de.assistant.uncertain} />}
                </h3>

                <ul className="flex flex-wrap gap-2">
                  {action.items.map((item, artikelIndex) => (
                    <li key={`${item.name}-${String(artikelIndex)}`} className="flex flex-col gap-1">
                      <span
                        data-testid="assistant-item"
                        data-name={item.name}
                        data-uncertain={item.needsClarification}
                        className={`inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 font-semibold ${
                          item.needsClarification ? "bg-honey-soft text-ink" : "bg-card text-ink shadow-[var(--shadow-card)]"
                        }`}
                      >
                        {item.name}
                        {item.quantity !== null && (
                          <span className="num text-sm text-ink-2">
                            {item.quantity}
                            {item.unit ? ` ${item.unit}` : ""}
                          </span>
                        )}
                      </span>

                      {item.alternatives.length > 0 && (
                        <span className="flex gap-1">
                          {item.alternatives.map((alternative) => (
                            <button
                              key={alternative}
                              type="button"
                              onClick={() => {
                                onReplace(aktionIndex, artikelIndex, alternative);
                              }}
                              data-testid="assistant-alternative"
                              className="min-h-[36px] rounded-full bg-card px-3 text-sm text-ink-2 shadow-[var(--shadow-card)]"
                            >
                              {alternative}
                            </button>
                          ))}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="assistant-discard"
            className="knopf-leise min-h-[56px] flex-1 px-4"
          >
            {de.assistant.discard}
          </button>
          <button
            type="button"
            onClick={onApply}
            disabled={leer}
            data-testid="assistant-apply"
            className="knopf-haupt min-h-[56px] flex-1 px-4 disabled:opacity-50"
          >
            {de.assistant.apply}
          </button>
        </div>
      </div>
    </div>
  );
}
